import { useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

interface SwipeBackOptions {
  enableSwipeBack?: boolean;
  onSwipeBack?: () => void;
  customBackHandler?: () => void;
  threshold?: number;
  edgeWidth?: number;
}

export const useMobileSwipeBack = ({
  enableSwipeBack = true,
  onSwipeBack,
  customBackHandler,
  threshold = 100,
  edgeWidth = 50
}: SwipeBackOptions = {}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const touchStartRef = useRef<{ x: number; y: number; time: number } | null>(null);
  const isSwipingRef = useRef(false);

  useEffect(() => {
    if (!enableSwipeBack) return;

    // Only enable on mobile devices
    const isMobile = window.innerWidth < 768;
    if (!isMobile) return;

    // Check if it's a PWA standalone mode
    const isStandalone = (window.navigator as any).standalone || 
                        window.matchMedia('(display-mode: standalone)').matches;

    const handleTouchStart = (e: TouchEvent) => {
      const touch = e.touches[0];
      const isLeftEdge = touch.clientX <= edgeWidth;
      
      if (isLeftEdge) {
        touchStartRef.current = {
          x: touch.clientX,
          y: touch.clientY,
          time: Date.now()
        };
        isSwipingRef.current = false;

        // Debug logging for PWA standalone mode
        if (isStandalone) {
          console.log('🔍 [PWA Debug] Swipe gesture started:', {
            x: touch.clientX,
            y: touch.clientY,
            edgeWidth,
            currentPath: location.pathname,
            isLeftEdge
          });
        }
      }
    };

    const handleTouchMove = (e: TouchEvent) => {
      if (!touchStartRef.current) return;

      const touch = e.touches[0];
      const deltaX = touch.clientX - touchStartRef.current.x;
      const deltaY = touch.clientY - touchStartRef.current.y;
      
      // Check if it's a horizontal swipe (more horizontal than vertical)
      const isHorizontalSwipe = Math.abs(deltaX) > Math.abs(deltaY);
      const isRightwardSwipe = deltaX > 0;
      
      if (isHorizontalSwipe && isRightwardSwipe && deltaX > 20) {
        isSwipingRef.current = true;
        
        // Prevent default to avoid potential scroll interference
        e.preventDefault();
        
        // Optional: Add visual feedback (could add edge glow effect here)
      }
    };

    const handleTouchEnd = (e: TouchEvent) => {
      if (!touchStartRef.current || !isSwipingRef.current) {
        touchStartRef.current = null;
        isSwipingRef.current = false;
        return;
      }

      const touch = e.changedTouches[0];
      const deltaX = touch.clientX - touchStartRef.current.x;
      const deltaY = touch.clientY - touchStartRef.current.y;
      const deltaTime = Date.now() - touchStartRef.current.time;
      
      // Check swipe criteria
      const isValidSwipe = 
        deltaX > threshold && // Minimum distance
        Math.abs(deltaY) < 150 && // Not too vertical
        deltaTime < 800; // Not too slow

      if (isValidSwipe) {
        // Debug logging for PWA standalone mode
        if (isStandalone) {
          console.log('🔍 [PWA Debug] Valid swipe detected:', {
            deltaX,
            deltaY,
            deltaTime,
            threshold,
            currentPath: location.pathname,
            willNavigateBack: true
          });
        }

        // Handle the back navigation
        if (customBackHandler) {
          // Only call custom handler, don't do browser navigation
          customBackHandler();
          // Custom handler takes full control - no browser back navigation
        } else if (onSwipeBack) {
          onSwipeBack();
        } else {
          // Default back behavior only when no custom handler
          if (window.history.length > 1) {
            navigate(-1);
          } else {
            // Fallback to dashboard if no history
            navigate('/dashboard');
          }
        }
      }

      // Reset touch tracking
      touchStartRef.current = null;
      isSwipingRef.current = false;
    };

    // Add touch event listeners to the document
    document.addEventListener('touchstart', handleTouchStart, { passive: true });
    document.addEventListener('touchmove', handleTouchMove, { passive: false });
    document.addEventListener('touchend', handleTouchEnd, { passive: true });

    // Cleanup function
    return () => {
      document.removeEventListener('touchstart', handleTouchStart);
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', handleTouchEnd);
    };
  }, [enableSwipeBack, navigate, location.pathname, onSwipeBack, customBackHandler, threshold, edgeWidth]);

  return {
    isSwipeBackEnabled: enableSwipeBack
  };
};

// Higher-order component to wrap pages with swipe back functionality
export const withMobileSwipeBack = <P extends object>(
  Component: React.ComponentType<P>,
  options?: SwipeBackOptions
) => {
  return (props: P) => {
    useMobileSwipeBack(options);
    return <Component {...props} />;
  };
}; 